import PropTypes from 'prop-types';
import React from 'react';
import { FormattedTime } from 'react-intl';
import shiftTimezone from 'tz-offsets';
import { CLASS_DETAIL_ROUTE } from 'features/InventoryDetail/ClassDetail.route.path';
import { VENUE_DETAIL_ROUTE } from 'features/InventoryDetail/InventoryDetail.route.path';
import { ScheduleCta } from 'features/InventoryDetail/Schedule/ScheduleCta';
import { LocalizableMessageShape } from 'components/LocalizableMessage/LocalizableMessage';
import { StarRating } from 'components/StarRating/StarRating';
import { Heading } from 'components/Heading/Heading';
import { Section } from 'components/Section/Section';

// WARNING
// If you change these props please also update TrackedScheduleItem
// to ensure that you do not break analytics
const ScheduleItem = ({
  activities,
  availabilityLabel,
  callToAction,
  classAlias,
  credits,
  duration,
  id,
  isPremium,
  name,
  neighborhood,
  reason,
  startTime,
  ratingsCount,
  ratingsMean,
  teacherName,
  timeZone,
  venueAlias,
  venueSubtitle,
  venueName,
  schedule,
  searchId,
  onCtaClicked,
  availableToReserve,
}) => {
  const isPremiumAndAvailable = isPremium && availableToReserve;
  const reasonTooltipText = !!reason && !isPremiumAndAvailable ? reason : undefined;

  return (
    <Section className="schedule-item">
      <div className="schedule-item__time">
        <FormattedTime value={new Date(shiftTimezone(startTime, timeZone) * 1e3)}>
          {time => <time>{time}</time>}
        </FormattedTime>
        {duration}
      </div>
      <div className="schedule-item__class">
        <Heading className="schedule-item__class__name zeta text--ellipsis" title={name}>
          <a
            href={`${CLASS_DETAIL_ROUTE}/${venueAlias}/${classAlias}/${id}?search-id=${searchId}`}
            target="_blank"
            rel="noopener noreferrer">
            {name}
          </a>
        </Heading>
        <div className="schedule-item__class__venue md-hidden">
          <a
            href={`${VENUE_DETAIL_ROUTE}/${venueAlias}?search-id=${searchId}`}
            target="_blank"
            rel="noopener noreferrer">
            {venueName}
          </a>
          <span className="text--bold"> &middot; </span>
          {neighborhood}
        </div>
        {teacherName && (
          <div className="schedule-item__class__teacher" title={teacherName}>
            {teacherName}
          </div>
        )}
        {!!ratingsMean && (
          <a
            className="schedule-item__class__rating"
            href={`${CLASS_DETAIL_ROUTE}/${venueAlias}/${classAlias}?search-id=${searchId}#reviews`}
            target="_blank"
            rel="noopener noreferrer">
            <StarRating rating={ratingsMean} count={ratingsCount} showRatingNumber singleStar />
          </a>
        )}
      </div>
      <div className="schedule-item__venue hidden md-block">
        <a
          href={`${VENUE_DETAIL_ROUTE}/${venueAlias}?search-id=${searchId}`}
          className="schedule-item__venue__name"
          target="_blank"
          rel="noopener noreferrer">
          {`${venueName}${venueSubtitle ? ` · ${venueSubtitle}` : ''}`}
        </a>
        <div className="schedule-item__venue__neighborhood" title={neighborhood}>
          {neighborhood}
        </div>
        <div className="schedule-item__venue__activities" title={activities}>
          {activities}
        </div>
      </div>
      <div className="schedule-item__action">
        <ScheduleCta
          action={callToAction}
          creditPrice={credits}
          onClick={onCtaClicked}
          schedule={schedule}
          tooltip={reasonTooltipText}
          className="bt--outline"
        />
        {availabilityLabel && !isPremium && <div className="schedule-item__label">{availabilityLabel}</div>}
      </div>
    </Section>
  );
};

ScheduleItem.propTypes = {
  activities: PropTypes.string,
  availabilityLabel: PropTypes.string,
  callToAction: PropTypes.string,
  classAlias: PropTypes.string,
  credits: PropTypes.number,
  duration: PropTypes.string,
  id: PropTypes.number,
  isPremium: PropTypes.bool,
  name: PropTypes.string,
  neighborhood: PropTypes.string,
  startTime: PropTypes.number,
  ratingsCount: PropTypes.string,
  ratingsMean: PropTypes.number,
  reason: LocalizableMessageShape,
  teacherName: PropTypes.string,
  timeZone: PropTypes.string,
  venueAlias: PropTypes.string,
  venueSubtitle: PropTypes.string,
  venueName: PropTypes.string,
  onCtaClicked: PropTypes.func,
  schedule: PropTypes.object,
  searchId: PropTypes.string,
  availableToReserve: PropTypes.bool,
};

export { ScheduleItem };
